<script lang="ts">
	import type { PageData } from './$types';
	import { classes } from '@thearc-hq/ui-kit/utils';
	import { getTranslate, T } from '@tolgee/svelte';
	import Breadcrumbs from '$lib/components/Breadcrumbs.svelte';
	import MagazinCard from '$lib/components/Magazin/MagazinCard.svelte';
	import ButtonPrimary from '$lib/components/Button/ButtonPrimary.svelte';
	import GenericPicker from '$lib/components/GenericPicker.svelte';
	import { page } from '$app/stores';
	import { goto } from '$app/navigation';
	import { resolveRoute } from '$app/paths';
	import { CONTENT_BRANCH_MAPPING } from '$lib/utils/magazin';

	export let data: PageData;

	let numStoriesToShow = 9;
	$: category = $page.params.branch ? $page.params.branch : 'all';
	let sortDirection: 'asc' | 'desc' = 'asc';
	let sortExpanded = false;

	$: sortedStories = data.stories.sort((a, b) => {
		if (sortDirection === 'desc') {
			return new Date(a.date).getTime() - new Date(b.date).getTime();
		} else {
			return new Date(b.date).getTime() - new Date(a.date).getTime();
		}
	});

	$: branches = Array.from(
		new Set(['all', ...data.stories.map((story) => CONTENT_BRANCH_MAPPING[story.category])])
	) as string[];

	$: filteredStories =
		category === 'all'
			? sortedStories.slice(0, numStoriesToShow)
			: sortedStories
					.filter((story) => CONTENT_BRANCH_MAPPING[story.category] === category)
					.slice(0, numStoriesToShow);

	const { t } = getTranslate();
</script>

<div class="box-x-wrapper w-full grid gap-11 md:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
	<Breadcrumbs class="sm:col-span-2 lg:col-span-3 mt-4 lg:mt-10" />
	<h1 class="sm:col-span-2 lg:col-span-3 text-h-32 sm:text-h-56 lg:text-h-72 lg:my-10">
		<T keyName="magazin_title" />
	</h1>
	<div
		class="flex flex-row items-stretch justify-between sm:col-span-2 lg:col-span-3 gap-4 lg:gap-0"
	>
		<div class="flex-1 overflow-x-auto overflow-y-visible scrollbar-hide">
			<div class="flex flex-row items-center justify-start gap-2 border-b-2 border-dark-quaternary">
				{#each branches as branch}
					{#if branch}
						<div class="flex flex-row text-h-16">
							<button
								disabled={branch === category}
								on:click={() => {
									numStoriesToShow = 9;
									goto(
										resolveRoute('/[locale]-[country]/magazin/[[branch]]', {
											...$page.params,
											branch: branch === 'all' ? undefined : branch
										}),
										{
											replaceState: true
										}
									);
								}}
							>
								<div
									class={classes(
										'px-4 py-2 font-bold uppercase transition-colors hover:bg-accent-primary_005',
										category === branch &&
											'text-accent-primary border-b-2 -mb-[2px] border-accent-primary'
									)}
								>
									<T keyName={`magazinCategory${branch}`} />
								</div>
							</button>
						</div>
					{/if}
				{/each}
			</div>
		</div>
		<div class="flex items-center lg:items-start flex-0 border-b-2 border-dark-quaternary">
			<GenericPicker class="p-2 rounded-md shadow-xl z-20">
				<div class="flex items-center gap-4 text-accent-primary text-h-16">
					<T keyName="search.sort" />
					<div
						class="i-sprenger-chevron-down transform transition duration-300 w-5 h-5"
						class:rotate-180={sortExpanded}
					></div>
				</div>
				<div slot="opened-content" class="px-2">
					<button on:click={() => (sortDirection = 'asc')} class="text-left whitespace-nowrap py-4">
						<T keyName="magazine_sort_date_asc" />
					</button>
					<button
						on:click={() => (sortDirection = 'desc')}
						class="text-left whitespace-nowrap py-4"
					>
						<T keyName="magazine_sort_date_desc" />
					</button>
				</div>
			</GenericPicker>
		</div>
	</div>
	{#each filteredStories as story}
		<MagazinCard blok={story} slug={story.slug} />
	{/each}

	{#if filteredStories.length >= numStoriesToShow}
		<div class="col-span-1 sm:col-span-2 lg:col-span-3 flex justify-center mt-8 mb-16">
			<ButtonPrimary text={$t('magazin_show_more')} on:click={() => (numStoriesToShow += 9)} />
		</div>
	{/if}
</div>
